import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import Cookies from 'universal-cookie';
import { clearAuth, getAuth, loadSession, passwordSignedIn } from './auth';
import { clearLocalStorage } from './analytics';
import { CONSTANTS } from 'constants';

const oas3Api: any = axios.create({
  baseURL: 'https://dev.mesn.arl-nus.org/service',
  headers: {
    'Content-Type': 'application/json'
  }
});

export { oas3Api };

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

type RetriableRequestConfig = AxiosRequestConfig & {
  _tokenRefreshRetried?: boolean;
};

function getApiRequestUrl(config: AxiosRequestConfig) {
  const apiUrl = new URL(import.meta.env.VITE_API_URL);
  const requestUrl = new URL(
    axios.getUri({ ...config, params: undefined }),
    apiUrl
  );
  // Only the configured backend may receive the portal's tokens and session credentials.
  if (
    requestUrl.origin !== apiUrl.origin ||
    requestUrl.username ||
    requestUrl.password
  ) {
    throw new Error('Refusing an API request outside the configured backend');
  }
  return requestUrl;
}

// Attach authentication centrally so callers do not handle cookies or CSRF themselves.
// Interceptor reference: https://axios-http.com/docs/interceptors
api.interceptors.request.use(
  async (config: any) => {
    // Public chart and parameter data do not need a session or browser credentials.
    const requestUrl = getApiRequestUrl(config);
    config.url = requestUrl.href;
    const path = requestUrl.pathname;
    if (
      /^\/public\/(latestData\/|latestGraphData$|parameters$|getMesnParameters$)/.test(
        path
      )
    ) {
      config.withCredentials = false;
      return config;
    }
    // Resolve the session before choosing NUS cookie authentication or password bearer tokens.
    await loadSession();
    const session = getAuth().session;
    if (session) config.headers['X-CSRF-Token'] = session.csrfToken;
    const cookies = new Cookies();
    const tokens = cookies.get('tokens');
    if (tokens && !session) {
      const { accessToken } = tokens;
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    config.headers['X-Active-Role'] = 'ROLE_USER';
    return config;
  },
  (err) => Promise.reject(err)
);

let tokenExpiredPromise: Promise<any> | null = null;
let forceTokenPromise: Promise<any> | null = null;

const refreshToken = async (
  originalRequestConfig: RetriableRequestConfig,
  refreshPromise: Promise<any> | null
) => {
  getApiRequestUrl(originalRequestConfig);
  const cookies = new Cookies();
  const tokens = cookies.get('tokens');
  if (tokens) {
    if (!refreshPromise) {
      refreshPromise = axios
        .post(import.meta.env.VITE_API_URL + '/public/refreshAccessToken', {
          refreshToken: tokens.refreshToken
        })
        .then((refreshRes) => {
          window.myAntsTag?.('SET', 'userId', getAuth().user?.userId);
          window.myAntsTag?.('EVENT', 'LOGIN');
          passwordSignedIn(refreshRes.data);
          return refreshRes.data;
        })
        .catch((refreshError) => {
          console.error(refreshError);
          clearAuth();
          clearLocalStorage();
          window.alert('Token Expired. Please login');
          // @ts-ignore
          window.location = '/login';
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    try {
      const newData = await refreshPromise;
      // Retry the original request with the new token
      if (!originalRequestConfig.headers) {
        originalRequestConfig.headers = {};
      }

      originalRequestConfig.headers.Authorization = `Bearer ${newData.accessToken}`;
      // Apply destination validation again and prevent a failed refresh from looping.
      return api.request({
        ...originalRequestConfig,
        _tokenRefreshRetried: true
      } as RetriableRequestConfig);
    } catch (err) {
      return Promise.reject(err);
    }
  } else {
    // @ts-ignore
    window.location = '/login';
  }
};

// Add a response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const data = error.response?.data as { message?: string } | undefined;

    // NUS session expiry requires another sign-in, not the password token refresh flow.
    if (status === 401 && getAuth().session) {
      clearAuth();
      clearLocalStorage();
      window.location.assign('/login');
      return Promise.reject(error);
    }

    if (
      (error.config as RetriableRequestConfig | undefined)?._tokenRefreshRetried
    ) {
      return Promise.reject(error);
    }

    if (status === 401 && data?.message === 'Token Expired') {
      return refreshToken(error.config!, tokenExpiredPromise);
    }

    if (status === 409 && data?.message === 'Force Access Token Refresh') {
      return refreshToken(error.config!, forceTokenPromise);
    }

    return Promise.reject(error);
  }
);

// The backend owns domain routing and the enable flag for both login and signup.
export async function continueWithEmail(
  email: string,
  signup = false
): Promise<boolean> {
  // End an unfinished NUS sign-in before starting a different login or registration.
  if (getAuth().session) {
    await api.post('/public/oidc/logout');
    clearAuth();
  }
  const normalized = email.trim().toLowerCase();
  const { data } = await api.post('/public/auth/method', { email: normalized });
  if (data.method === 'password') return true;
  if (data.method !== 'nus') throw new Error('Invalid authentication method');
  const response = await api.post('/public/oidc/start', {
    email: normalized,
    portal: 'ombak'
  });
  // Navigation intent only; the backend session supplies the verified identity.
  sessionStorage.setItem('nus-signup', String(signup));
  window.location.assign(response.data.authorizationUrl);
  return false;
}

const getTypedApiData = <T = any>(data: any) => data as T;

// get - latestData
type GetLatestDataResponse = {
  [key: string]: {
    value: unknown;
    unit: unknown;
  };
} & {
  latestTs: string;
};

// post - latestGraphData
type PostLatestGraphDataResponse = Array<{
  qcValue: any;
  timestamp: any;
}>;

export const ROUTES = {
  GET_CONSTANTS: {
    apiPath: `/public/constants`
    // getTypedApiData: getTypedApiData
  },
  GET_LATEST_DATA: {
    apiPath: `/public/latestData/:location`,
    getTypedApiData: getTypedApiData<GetLatestDataResponse>
  },
  POST_LATEST_GRAPH_DATA: {
    apiPath: `/public/latestGraphData`,
    getTypedApiData: getTypedApiData<PostLatestGraphDataResponse>
  }
};

export default api;
